import {useMemo} from 'react'
import {ALL_ROUTES} from '~/src/routes/routeConfig'
import {isRouteDeniedByPermissions} from '~/src/routes/routeAccess'
import {useAppSelector} from '~/src/store/store-hooks'
import type {BackendRolePermissions} from '~/src/utils/permissions'
import type {NotificationDestinationOptions} from './notificationDestination'

/** The routes a job order invitation notification opens (STFFLG-4184), by routeConfig id. */
const PENDING_ORDERS_ROUTE_ID = 'agency-service-order-pending'
const JOB_ORDER_PAGE_ROUTE_ID = 'job-order-summary'

export type InvitationNotificationAccess = Required<
  Pick<NotificationDestinationOptions, 'canOpenPendingOrders' | 'canOpenJobOrders'>
>

function routeOpens(
  routeId: string,
  backendPermissions: BackendRolePermissions | null | undefined,
): boolean {
  const route = ALL_ROUTES.find((r) => r.id === routeId)
  return !!route && !isRouteDeniedByPermissions(route, backendPermissions)
}

/**
 * Whether the role can open the pages an invitation notification points at: Manage Orders ›
 * Pending Orders (manage_orders > service_orders) and a job order's page (manage_orders >
 * job_orders). The route guard's own answer (routeAccess.ts), so a notification never offers a page
 * that sends the user to /unauthorized; with no role in the store yet it answers as the guard does
 * (open, the API decides).
 */
export function invitationNotificationAccess(
  backendPermissions: BackendRolePermissions | null | undefined,
): InvitationNotificationAccess {
  return {
    canOpenPendingOrders: routeOpens(PENDING_ORDERS_ROUTE_ID, backendPermissions),
    canOpenJobOrders: routeOpens(JOB_ORDER_PAGE_ROUTE_ID, backendPermissions),
  }
}

/** invitationNotificationAccess for the signed-in role. */
export function useInvitationNotificationAccess(): InvitationNotificationAccess {
  const backendPermissions = useAppSelector((state) => state.root.backendRolePermissions)
  return useMemo(() => invitationNotificationAccess(backendPermissions), [backendPermissions])
}
